"use client";

import FillInTheGapCanvas from "@/components/FillInTheGapCanvas";
import { ResponsiveLanguagePicker } from "@/components/language/ResponsiveLanguagePicker";
import languages from "@/data/languages.json";
import { getSeoPracticeLinksForLanguage } from "@/lib/seo/internalLinks";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Brain,
  Check,
  ChevronDown,
  Eye,
  Flame,
  Gift,
  Globe,
  Sparkles,
  Target,
  Trophy,
  X,
  Zap,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, { useMemo, useState } from "react";

type LanguageLevel = "absolute_beginner" | "beginner" | "intermediate" | "advanced";

const LEVELS: { value: LanguageLevel; label: string; description: string }[] = [
  { value: "absolute_beginner", label: "Absolute Beginner", description: "A0" },
  { value: "beginner", label: "Beginner", description: "A1-A2" },
  { value: "intermediate", label: "Intermediate", description: "B1-B2" },
  { value: "advanced", label: "Advanced", description: "C1-C2" },
];

// Sample sentences for the demo widget - one per language
const DEMO_SENTENCES: Record<
  string,
  { sentence: string; answer: string; translation: string; options: string[] }
> = {
  "Spanish (Spain)": {
    sentence: "El gato ___ en el jardín.",
    answer: "está",
    translation: "The cat is in the garden.",
    options: ["está", "son", "tiene", "hace"],
  },
  French: {
    sentence: "Je ___ français.",
    answer: "parle",
    translation: "I speak French.",
    options: ["parle", "mange", "vois", "suis"],
  },
  German: {
    sentence: "Ich ___ Deutsch.",
    answer: "spreche",
    translation: "I speak German.",
    options: ["spreche", "esse", "trinke", "bin"],
  },
  Italian: {
    sentence: "Io ___ italiano.",
    answer: "parlo",
    translation: "I speak Italian.",
    options: ["parlo", "mangio", "bevo", "sono"],
  },
  Japanese: {
    sentence: "\u79c1\u306f\u65e5\u672c\u8a9e\u3092___\u3002",
    answer: "\u8a71\u3057\u307e\u3059",
    translation: "I speak Japanese.",
    options: [
      "\u8a71\u3057\u307e\u3059",
      "\u98df\u3079\u307e\u3059",
      "\u98f2\u307f\u307e\u3059",
      "\u898b\u307e\u3059",
    ],
  },
  "Mandarin Chinese": {
    sentence: "\u6211___\u4e2d\u6587\u3002",
    answer: "\u8bf4",
    translation: "I speak Chinese.",
    options: ["\u8bf4", "\u5403", "\u559d", "\u770b"],
  },
  "Portuguese (Brazil)": {
    sentence: "Eu ___ português.",
    answer: "falo",
    translation: "I speak Portuguese.",
    options: ["falo", "como", "bebo", "sou"],
  },
  Korean: {
    sentence: "\uc800\ub294 \ud55c\uad6d\uc5b4\ub97c ___.",
    answer: "\ud569\ub2c8\ub2e4",
    translation: "I speak Korean.",
    options: [
      "\ud569\ub2c8\ub2e4",
      "\uba39\uc2b5\ub2c8\ub2e4",
      "\ub9c8\uc2ed\ub2c8\ub2e4",
      "\ubd05\ub2c8\ub2e4",
    ],
  },
  default: {
    sentence: "I ___ learning languages.",
    answer: "enjoy",
    translation: "Practice makes perfect!",
    options: ["enjoy", "like", "love", "hate"],
  },
};

// FAQ data for the micro tool
const FAQS = [
  {
    question: "Is this really free?",
    answer:
      "Yes, the ChickyTutor Fill-in-the-Gap tool is a completely free resource for the language learning community. No subscription required, no hidden fees, and no limits on practice sessions.",
  },
  {
    question: "What languages are supported?",
    answer:
      "Over 70 languages including Spanish, French, German, Italian, Japanese, Korean, Mandarin Chinese, Portuguese, Arabic, Russian, Hindi, Thai, Vietnamese, and many more. New languages are added regularly.",
  },
  {
    question: "Do I need an account?",
    answer:
      "No, you can start practicing immediately without any registration. However, creating a free account allows you to save your progress across devices and track your learning journey over time.",
  },
  {
    question: "Can I practice on my phone?",
    answer:
      "Yes! ChickyTutor is fully responsive and works great on mobile devices. You can even install it as a Progressive Web App (PWA) for an app-like experience.",
  },
  {
    question: "How does the adaptive difficulty work?",
    answer:
      "Our system tracks your performance and automatically adjusts sentence complexity. Get answers right, and you'll see harder sentences. Make mistakes, and we'll provide easier practice to build confidence.",
  },
];

type FillInTheGapDetails = {
  focus: string;
  sampleSentence: string;
  answer: string;
  explanation: string;
  targets: string[];
};

const FILL_IN_THE_GAP_DETAILS: Record<string, FillInTheGapDetails> = {
  Amharic: {
    focus: "Build everyday Amharic vocabulary while noticing sentence endings and word order.",
    sampleSentence: "I drink coffee in the morning.",
    answer: "coffee",
    explanation:
      "Use gaps for common nouns and verbs first, then move into sentence endings once the base pattern feels familiar.",
    targets: ["daily nouns", "common verbs", "sentence endings"],
  },
  Danish: {
    focus: "Practice Danish word order, common verbs, and small connector words in real sentences.",
    sampleSentence: "I speak Danish every day.",
    answer: "speak",
    explanation:
      "Danish learners often need repeated exposure to verb placement, short adverbs, and natural everyday phrasing.",
    targets: ["word order", "verbs", "adverbs"],
  },
  French: {
    focus:
      "Use French gaps to review articles, gender agreement, verb forms, and everyday vocabulary.",
    sampleSentence: "Je parle francais avec mon ami.",
    answer: "parle",
    explanation:
      "A good French cloze drill should mix meaning with grammar, especially articles, agreement, and common present-tense verbs.",
    targets: ["articles", "agreement", "present tense"],
  },
  Malay: {
    focus:
      "Practice Malay word choice and reusable sentence patterns without heavy conjugation overhead.",
    sampleSentence: "Saya belajar bahasa Melayu setiap hari.",
    answer: "belajar",
    explanation:
      "Malay fill-in-the-gap practice is useful for choosing the right content word and getting comfortable with natural sentence rhythm.",
    targets: ["word choice", "daily phrases", "sentence rhythm"],
  },
  Swahili: {
    focus:
      "Review Swahili noun classes, agreement, verb forms, and high-frequency daily vocabulary.",
    sampleSentence: "Ninasoma Kiswahili kila siku.",
    answer: "Ninasoma",
    explanation:
      "Swahili gaps work best when they connect vocabulary to agreement patterns instead of drilling isolated word lists.",
    targets: ["noun classes", "agreement", "verb prefixes"],
  },
};

const DEFAULT_FILL_IN_THE_GAP_DETAILS: FillInTheGapDetails = {
  focus: "Build vocabulary in context before moving into longer speaking and writing practice.",
  sampleSentence: "I practice this language every day.",
  answer: "practice",
  explanation:
    "Use fill-in-the-gap drills to connect word meaning with sentence position, then reuse the same phrases in speaking practice.",
  targets: ["vocabulary", "sentence patterns", "review"],
};

function getFillInTheGapDetails(languageName: string): FillInTheGapDetails {
  return FILL_IN_THE_GAP_DETAILS[languageName] ?? DEFAULT_FILL_IN_THE_GAP_DETAILS;
}

// Lead magnet popup component
function LeadMagnetPopup({
  isOpen,
  onClose,
  selectedLanguage,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedLanguage: string;
}) {
  if (!isOpen) return null;
  const seoLinks = getSeoPracticeLinksForLanguage(selectedLanguage);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-gradient-to-br from-[#1c202e] to-[#252a3d] rounded-2xl p-8 max-w-md w-full border border-white/10 shadow-2xl"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>

          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 mb-4">
              <Gift size={32} className="text-white" />
            </div>

            <h3 className="text-2xl font-bold text-white mb-2">You're on fire! ðŸ”¥</h3>
            <p className="text-gray-400 mb-6">
              Enjoying Fill-in-the-Gap practice? Take your {selectedLanguage || "language"} learning
              to the next level with our full AI tutor.
            </p>

            <div className="space-y-3">
              <Link
                href={seoLinks.aiTutorPath ?? "/"}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-bold rounded-xl transition-all"
              >
                <Sparkles size={18} />
                Try Full AI Tutor Free
              </Link>

              <button
                onClick={onClose}
                className="w-full py-3 px-4 text-gray-400 hover:text-white transition-colors text-sm"
              >
                Continue practicing here
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              - Voice conversations - Pronunciation feedback - 70+ languages
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Hero Demo Widget - Non-interactive example matching fill-in-the-gap style
function HeroDemoWidget({
  selectedLanguage,
  onLanguageChange,
  onStartFullPractice,
  startLabel = "Start Interactive Practice",
}: {
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
  onStartFullPractice: () => void;
  startLabel?: string;
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const currentSentence = DEMO_SENTENCES[selectedLanguage] || DEMO_SENTENCES.default;

  // Shuffle options for display (but keep answer highlighted)
  const shuffledOptions = useMemo(() => {
    const opts = [...currentSentence.options];
    // Simple shuffle
    for (let i = opts.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [opts[i], opts[j]] = [opts[j], opts[i]];
    }
    return opts;
  }, [currentSentence.options]);

  return (
    <div className="bg-[#1a1d2e] rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
      {/* Header bar matching fill-in-the-gap style */}
      <div className="bg-[#252a3d] px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
            <BookOpen size={16} className="text-white" />
          </div>
          <span className="text-white font-medium text-sm">Fill-in-the-Gap</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span className="px-2 py-1 bg-white/5 rounded">Level 1</span>
          <span className="px-2 py-1 bg-orange-500/20 text-orange-300 rounded">Preview</span>
        </div>
      </div>

      <div className="p-6">
        {/* Language Selector - All 70+ languages */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Select Language to Preview
          </label>
          <ResponsiveLanguagePicker
            open={isDropdownOpen}
            onOpenChange={setIsDropdownOpen}
            trigger={
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left transition-colors hover:bg-white/10"
              >
                <span className="font-medium text-white">{selectedLanguage}</span>
                <ChevronDown
                  size={20}
                  className={`text-gray-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
            }
            languages={languages as Record<string, string>}
            selectedLanguageCode={(languages as Record<string, string>)[selectedLanguage] ?? ""}
            onSelectLanguage={(code) => {
              const entry = Object.entries(languages as Record<string, string>).find(
                ([, languageCode]) => languageCode === code
              );
              if (entry) onLanguageChange(entry[0]);
            }}
            searchTerm={searchQuery}
            onSearchTermChange={setSearchQuery}
            title="Choose a practice language"
          />
        </div>

        {/* Sentence Display - matching fill-in-the-gap style */}
        <motion.div
          key={selectedLanguage}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 rounded-xl p-5 mb-4 border border-white/5"
        >
          <p className="text-xl sm:text-2xl text-white font-medium mb-3 leading-relaxed">
            {currentSentence.sentence.split("___").map((part: string, i: number) => (
              <React.Fragment key={i}>
                {part}
                {i === 0 && (
                  <span className="inline-block min-w-[80px] px-2 py-1 mx-1 bg-orange-500/20 border-2 border-dashed border-orange-400 rounded-lg text-orange-300">
                    ?
                  </span>
                )}
              </React.Fragment>
            ))}
          </p>
          <p className="text-sm text-gray-400 italic">{currentSentence.translation}</p>
        </motion.div>

        {/* Word Chips - matching fill-in-the-gap style */}
        <div className="mb-6">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">
            Choose the correct word:
          </p>
          <div className="flex flex-wrap gap-2">
            {shuffledOptions.map((option: string, index: number) => (
              <motion.div
                key={option}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className={`px-4 py-2.5 rounded-xl font-medium text-sm cursor-default transition-all ${
                  option === currentSentence.answer
                    ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg"
                    : "bg-white/10 text-gray-300 border border-white/10"
                }`}
              >
                {option}
              </motion.div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">Correct answer highlighted for preview</p>
        </div>

        {/* Start Practice Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onStartFullPractice}
          className="w-full py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
        >
          <Zap size={20} />
          <span>{startLabel}</span>
          <ArrowRight size={18} />
        </motion.button>
      </div>
    </div>
  );
}

// Comparison Table Component
function ComparisonTable() {
  const features = [
    {
      feature: "Pricing",
      clozemaster: "Subscription for Pro ($8-12/mo)",
      chickytutor: "100% Free",
      highlight: true,
    },
    {
      feature: "Account Required",
      clozemaster: "Yes, required",
      chickytutor: "No, instant start",
      highlight: true,
    },
    {
      feature: "Exercises",
      clozemaster: "Limited on free plan",
      chickytutor: "Unlimited exercises",
      highlight: true,
    },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-4 px-4 text-gray-400 font-medium">Feature</th>
            <th className="text-center py-4 px-4 text-gray-400 font-medium">Clozemaster</th>
            <th className="text-center py-4 px-4 text-orange-400 font-medium">ChickyTutor</th>
          </tr>
        </thead>
        <tbody>
          {features.map((row, index) => (
            <tr
              key={index}
              className={`border-b border-white/5 ${row.highlight ? "bg-orange-500/5" : ""}`}
            >
              <td className="py-4 px-4 text-white font-medium">{row.feature}</td>
              <td className="py-4 px-4 text-center text-gray-400">{row.clozemaster}</td>
              <td className="py-4 px-4 text-center">
                <span className="inline-flex items-center gap-1 text-green-400">
                  <Check size={14} />
                  {row.chickytutor}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// FAQ Component with Schema
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="space-y-4">
      {FAQS.map((faq, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.05 }}
          className="bg-white/5 rounded-xl border border-white/10 overflow-hidden"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
          >
            <span className="font-semibold text-white pr-4">{faq.question}</span>
            <ChevronDown
              className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                openIndex === index ? "rotate-180" : ""
              }`}
            />
          </button>

          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-4 text-gray-400">{faq.answer}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>
  );
}

type FillInTheGapToolClientProps = {
  initialLanguage?: string;
  pageMode?: "general" | "language" | "level-test";
};

// Main Page Component
export default function FillInTheGapToolClient({
  initialLanguage,
  pageMode = "general",
}: FillInTheGapToolClientProps) {
  const languageMap = languages as Record<string, string>;
  const defaultLanguage = "Spanish (Spain)";
  const resolvedInitialLanguage =
    initialLanguage && languageMap[initialLanguage] ? initialLanguage : defaultLanguage;
  const [selectedLanguage, setSelectedLanguage] = useState(resolvedInitialLanguage);
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(
    languageMap[resolvedInitialLanguage] ?? "es-ES"
  );
  const [selectedLevel, setSelectedLevel] = useState<LanguageLevel>("beginner");
  const [hasStarted, setHasStarted] = useState(false);
  const [showLeadMagnet, setShowLeadMagnet] = useState(false);
  const isLanguagePage = pageMode === "language";
  const isLevelTestPage = pageMode === "level-test";
  const seoLinks = useMemo(
    () => getSeoPracticeLinksForLanguage(selectedLanguage),
    [selectedLanguage]
  );
  const speakingLevelPath = seoLinks.languageTargetPath ?? seoLinks.speakingPracticePath ?? "/";
  const primaryAppPath = isLevelTestPage ? speakingLevelPath : (seoLinks.aiTutorPath ?? "/");
  const gapDetails = getFillInTheGapDetails(selectedLanguage);

  const handleLanguageChange = (lang: string) => {
    setSelectedLanguage(lang);
    const code = languageMap[lang];
    if (code) setSelectedLanguageCode(code);
  };

  const handleStartPractice = () => {
    setHasStarted(true);
  };

  const handleClose = () => {
    setHasStarted(false);
  };

  // If exercises have started, show the full-screen canvas
  if (hasStarted) {
    return (
      <div className="relative">
        <FillInTheGapCanvas
          selectedLanguage={selectedLanguage}
          selectedLanguageCode={selectedLanguageCode}
          selectedLanguageLevel={selectedLevel}
          onClose={handleClose}
        />
        {/* Floating CTA */}
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 2, type: "spring" }}
          className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:w-auto"
        >
          <Link
            href={primaryAppPath}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
          >
            <Sparkles size={18} />
            <span>{isLevelTestPage ? "Test Speaking Level" : "Try Full AI Tutor"}</span>
            <ArrowRight size={16} />
          </Link>
        </motion.div>

        <LeadMagnetPopup
          isOpen={showLeadMagnet}
          onClose={() => setShowLeadMagnet(false)}
          selectedLanguage={selectedLanguage}
        />
      </div>
    );
  }

  return (
    <>
      <main className="min-h-screen bg-gradient-to-b from-[#1a1d2e] via-[#1c202e] to-[#181b28]">
        {/* Header */}
        <header className="p-4 sm:p-6 flex items-center justify-between border-b border-white/5">
          <Link href="/" className="flex items-center gap-2">
            <Image
              src="/ui/Chickytutor_bird.png"
              alt="ChickyTutor"
              width={40}
              height={40}
              className="rounded-full"
            />
            <span className="text-white font-bold text-lg hidden sm:inline">ChickyTutor</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              href={
                isLevelTestPage
                  ? "/tools/fill-in-the-gap-language-practice"
                  : "/blog/clozemaster-alternative-fill-in-the-gap"
              }
              className="text-sm text-gray-400 hover:text-white transition-colors hidden sm:block"
            >
              {isLevelTestPage ? "Fill-in-the-gap tool" : "Read Full Guide"}
            </Link>
            <Link
              href={primaryAppPath}
              className="text-sm text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-1"
            >
              <span>{isLevelTestPage ? "Test speaking level" : "Full App"}</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Headlines */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-6">
                <Zap size={14} />
                <span>Free practice - no sign-up required</span>
              </div>

              {isLevelTestPage ? (
                <>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
                    Test Your{" "}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
                      {selectedLanguage}
                    </span>{" "}
                    Level
                  </h1>

                  <p className="text-xl text-gray-400 mb-8 max-w-xl mx-auto lg:mx-0">
                    Use visual fill-in-the-gap practice to check your starting point, get instant
                    feedback, and let sentence difficulty adapt as you answer.
                  </p>

                  <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-3 mb-8">
                    <button
                      onClick={handleStartPractice}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                    >
                      <BookOpen size={18} />
                      <span>Test fill-in-the-gap level</span>
                    </button>
                    <Link
                      href={speakingLevelPath}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold rounded-xl transition-colors"
                    >
                      <Sparkles size={18} />
                      <span>Test speaking level</span>
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </>
              ) : isLanguagePage ? (
                <>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
                    Practice{" "}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
                      {selectedLanguage}
                    </span>{" "}
                    with Visual Fill-in-the-Gap Exercises
                  </h1>

                  <p className="text-xl text-gray-400 mb-8 max-w-xl mx-auto lg:mx-0">
                    Build {selectedLanguage} vocabulary in context with free, interactive sentence
                    practice and instant feedback.
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
                    Master 70+ Languages with{" "}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
                      Visual Fill-in-the-Gap
                    </span>{" "}
                    Practice
                  </h1>

                  <p className="text-xl text-gray-400 mb-8 max-w-xl mx-auto lg:mx-0">
                    The visual-first Clozemaster alternative. Master vocabulary in context with
                    instant feedback-no account required to start.
                  </p>
                </>
              )}

              {/* Trust badges */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Globe size={16} className="text-orange-400" />
                  <span>70+ Languages</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Eye size={16} className="text-orange-400" />
                  <span>Visual Learning</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Trophy size={16} className="text-orange-400" />
                  <span>Progress Tracking</span>
                </div>
              </div>
            </motion.div>

            {/* Right: Interactive Widget */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <HeroDemoWidget
                selectedLanguage={selectedLanguage}
                onLanguageChange={handleLanguageChange}
                onStartFullPractice={handleStartPractice}
                startLabel={isLevelTestPage ? "Start Level Test" : "Start Interactive Practice"}
              />
            </motion.div>
          </div>
        </section>

        {isLanguagePage && (
          <section className="max-w-6xl mx-auto px-4 py-16 border-t border-white/5">
            <div className="grid lg:grid-cols-[1fr_0.85fr] gap-8 items-start">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-300 text-sm font-medium mb-4">
                  <Target size={14} />
                  <span>{selectedLanguage} focus</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  What to practice first in {selectedLanguage}
                </h2>
                <p className="text-gray-400 text-lg mb-6">{gapDetails.focus}</p>
                <div className="flex flex-wrap gap-2">
                  {gapDetails.targets.map((target) => (
                    <span
                      key={target}
                      className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300"
                    >
                      {target}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.08 }}
                className="bg-white/5 rounded-2xl border border-white/10 p-6"
              >
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">
                  Example cloze pattern
                </p>
                <div className="rounded-xl bg-[#1a1d2e] border border-white/10 p-5 mb-4">
                  <p className="text-xl text-white font-semibold leading-relaxed">
                    {gapDetails.sampleSentence.replace(gapDetails.answer, "___")}
                  </p>
                  <p className="text-sm text-orange-300 mt-3">Answer: {gapDetails.answer}</p>
                </div>
                <p className="text-gray-400 mb-5">{gapDetails.explanation}</p>
                <div className="grid sm:grid-cols-4 gap-3">
                  <Link
                    href={seoLinks.aiHubPath}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10"
                  >
                    <Sparkles size={16} />
                    <span>AI tutors</span>
                  </Link>
                  {seoLinks.writingPracticePath && (
                    <Link
                      href={seoLinks.writingPracticePath}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10"
                    >
                      <BookOpen size={16} />
                      <span>Write</span>
                    </Link>
                  )}
                  {seoLinks.speakingPracticePath && (
                    <Link
                      href={seoLinks.speakingPracticePath}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10"
                    >
                      <Sparkles size={16} />
                      <span>Speak</span>
                    </Link>
                  )}
                  {seoLinks.languageTargetPath && (
                    <Link
                      href={seoLinks.languageTargetPath}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-orange-500/30 bg-orange-500/10 px-4 py-3 text-sm font-semibold text-orange-200 hover:bg-orange-500/20"
                    >
                      <ArrowRight size={16} />
                      <span>Learn</span>
                    </Link>
                  )}
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {/* Comparison Section */}
        {!isLevelTestPage && (
          <section className="max-w-4xl mx-auto px-4 py-16 border-t border-white/5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Why Choose ChickyTutor Over Clozemaster?
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                See how our free micro tool compares to Clozemaster's subscription model.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/5 rounded-2xl border border-white/10 p-6"
            >
              <ComparisonTable />
            </motion.div>
          </section>
        )}

        {/* Core Features Section */}
        <section className="max-w-6xl mx-auto px-4 py-16 border-t border-white/5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {isLevelTestPage ? "How the Level Test Works" : "Core Features of the Micro Tool"}
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {isLevelTestPage
                ? "Start from the closest CEFR band, answer real sentence gaps, and use the visual feedback to find the level where practice feels challenging but doable."
                : "Everything you need for effective vocabulary practice, completely free."}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Target className="text-orange-400" size={32} />,
                title: "Contextual Mastery",
                description:
                  "We don't just teach words; we teach how they live in sentences. Learn vocabulary in its natural habitat for better retention.",
              },
              {
                icon: <Eye className="text-pink-400" size={32} />,
                title: "Visual Reinforcement",
                description:
                  "Color-coded feedback helps memory retention. Green for correct, yellow for close, red for incorrect - learn through visual cues.",
              },
              {
                icon: <Flame className="text-yellow-400" size={32} />,
                title: "Gamified Progress",
                description:
                  "Track your streak and accuracy as you practice. Level up from 1 to 100 as you master more challenging sentences.",
              },
              {
                icon: <Brain className="text-purple-400" size={32} />,
                title: "Adaptive Difficulty",
                description:
                  "Our system adjusts to your level automatically. Get answers right for harder sentences, make mistakes for easier practice.",
              },
              {
                icon: <Globe className="text-blue-400" size={32} />,
                title: "70+ Languages",
                description:
                  "From Spanish to Japanese, Arabic to Finnish. Practice virtually any language with native-quality content.",
              },
              {
                icon: <Zap className="text-green-400" size={32} />,
                title: "Instant Start",
                description:
                  "No sign-up required to begin. Start practicing in seconds - create an account later to save progress.",
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 rounded-xl border border-white/10 p-6 hover:bg-white/10 transition-colors"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Level Selection CTA */}
        <section className="max-w-2xl mx-auto px-4 py-16 border-t border-white/5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-orange-500/10 to-pink-500/10 rounded-2xl border border-orange-500/20 p-8 text-center"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {isLevelTestPage
                ? `Ready to Test Your ${selectedLanguage} Level?`
                : "Ready for Full Practice?"}
            </h2>
            <p className="text-gray-400 mb-6">
              {isLevelTestPage
                ? "Pick the level that feels closest, then answer visual fill-in-the-gap exercises to calibrate up or down."
                : "Select your level and start unlimited fill-in-the-gap exercises."}
            </p>

            {/* Level Selection */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {LEVELS.map((level) => (
                <button
                  key={level.value}
                  onClick={() => setSelectedLevel(level.value)}
                  className={`p-3 rounded-xl border transition-all ${
                    selectedLevel === level.value
                      ? "bg-orange-500/20 border-orange-500/50 text-white"
                      : "bg-white/5 border-white/10 text-gray-400 hover:bg-white/10"
                  }`}
                >
                  <span className="block font-medium text-sm">{level.label}</span>
                  <span className="block text-xs opacity-60">{level.description}</span>
                </button>
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleStartPractice}
              className="w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 mx-auto"
            >
              <BookOpen size={20} />
              <span>{isLevelTestPage ? "Start Level Test" : "Start Full Practice"}</span>
            </motion.button>
          </motion.div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-3xl mx-auto px-4 py-16 border-t border-white/5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-400 text-lg">
              Everything you need to know about our Fill-in-the-Gap tool.
            </p>
          </motion.div>

          <FAQSection />
        </section>

        {/* Final CTA */}
        <section className="border-t border-white/5 py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                {isLevelTestPage ? "Want to Test Speaking Too?" : "Want to Practice Speaking Too?"}
              </h2>
              <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
                {isLevelTestPage
                  ? `Use the ${selectedLanguage} speaking page to start the full ChickyTutor voice flow with the language already selected.`
                  : "Combine fill-in-the-gap practice with AI voice conversations for complete language mastery."}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={primaryAppPath}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  <Sparkles size={18} />
                  <span>{isLevelTestPage ? "Test Speaking Level" : "Try Full AI Tutor"}</span>
                  <ArrowRight size={16} />
                </Link>
                <Link
                  href="/blog/clozemaster-alternative-fill-in-the-gap"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-xl transition-colors"
                >
                  <BookOpen size={18} />
                  <span>Read Full Comparison Guide</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/5 py-8 px-4">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Image
                src="/ui/Chickytutor_bird.png"
                alt="ChickyTutor"
                width={32}
                height={32}
                className="rounded-full"
              />
              <span className="text-gray-400 text-sm">
                (c) 2026 ChickyTutor. Free language learning for everyone.
              </span>
            </div>
            <div className="flex items-center gap-6">
              <Link
                href="/terms"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Terms
              </Link>
              <Link
                href="/blog"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Blog
              </Link>
              <Link href="/" className="text-gray-400 hover:text-white text-sm transition-colors">
                Home
              </Link>
            </div>
          </div>
        </footer>

        {/* Lead Magnet Popup */}
        <LeadMagnetPopup
          isOpen={showLeadMagnet}
          onClose={() => setShowLeadMagnet(false)}
          selectedLanguage={selectedLanguage}
        />
      </main>
    </>
  );
}
